import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { phappsRequest } from '@/services/apiService';
import {
  API_PATHS,
  buildChemLinkAdminPath,
  CHEM_LINK_ADMIN_ENDPOINTS,
} from '@/config/apiConstants';
import { MEDIDROP_COLORS } from '@/config/colors';

const ORANGE = MEDIDROP_COLORS.ORANGE;

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Create/Edit Group dialog ────────────────────────────────────────────────

function GroupFormDialog({ open, existing, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.GroupName ?? '');
      setError('');
    }
  }, [open, existing]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      if (existing) {
        await phappsRequest(
          buildChemLinkAdminPath(`Groups/${existing.ChemLinkGroupId}`),
          'PUT',
          { groupName: name.trim() },
        );
      } else {
        await phappsRequest(API_PATHS.CHEM_LINK_ADMIN_GROUPS, 'POST', { groupName: name.trim() });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: ORANGE, fontWeight: 700 }}>
        {existing ? 'Rename group' : 'New ChemLink group'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{ backgroundColor: ORANGE, '&:hover': { backgroundColor: '#d4613c' } }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Chemist search + add dialog ─────────────────────────────────────────────

function AddChemistDialog({ open, onClose, onSearch, onAdd }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const debouncedQ = useDebounce(q, 300);

  useEffect(() => {
    if (open) { setQ(''); setResults([]); }
  }, [open]);

  useEffect(() => {
    if (!debouncedQ.trim()) { setResults([]); return; }
    let active = true;
    setSearching(true);
    onSearch(debouncedQ.trim())
      .then((r) => { if (active) setResults(r); })
      .finally(() => { if (active) setSearching(false); });
    return () => { active = false; };
  }, [debouncedQ, onSearch]);

  const handleAdd = async (item) => {
    setAdding(item.id);
    try { await onAdd(item); setResults((prev) => prev.filter((r) => r.id !== item.id)); }
    catch {}
    finally { setAdding(null); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: ORANGE, fontWeight: 700 }}>Add chemist to group</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={14} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1, mb: 2 }}
        />
        {results.length === 0 && debouncedQ.trim() && !searching && (
          <Typography variant="body2" color="text.secondary">No results found.</Typography>
        )}
        {results.map((r) => (
          <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
              {r.subtitle && <Typography variant="caption" color="text.secondary">{r.subtitle}</Typography>}
            </Box>
            <Button
              size="small"
              variant="outlined"
              disabled={adding === r.id}
              onClick={() => handleAdd(r)}
              sx={{ ml: 2, flexShrink: 0, borderColor: ORANGE, color: ORANGE }}
            >
              {adding === r.id ? <CircularProgress size={14} color="inherit" /> : 'Add'}
            </Button>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Group detail ─────────────────────────────────────────────────────────────

function GroupDetail({ group, onGroupChange }) {
  const [chemists, setChemists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState('');

  const gid = group.ChemLinkGroupId;

  const loadChemists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phappsRequest(
        buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.GROUP_CHEMISTS(gid)),
        'GET',
      );
      setChemists(res?.Chemists ?? []);
    } catch { setError('Failed to load chemists'); }
    finally { setLoading(false); }
  }, [gid]);

  useEffect(() => { loadChemists(); }, [loadChemists]);

  const searchChemists = useCallback(async (q) => {
    const res = await phappsRequest(API_PATHS.CHEM_LINK_ADMIN_CHEMIST_SEARCH, 'GET', null, { q });
    return (res?.Chemists ?? []).map((c) => ({
      id: c.ChemistID,
      name: c.ChemistName,
      subtitle: c.ChemistAddress ?? '',
    }));
  }, []);

  const addChemist = async ({ id }) => {
    await phappsRequest(
      buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.GROUP_CHEMISTS(gid)),
      'POST',
      { chemistId: id },
    );
    await loadChemists();
  };

  const removeChemist = async (chemistId) => {
    await phappsRequest(
      buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.GROUP_CHEMIST(gid, chemistId)),
      'DELETE',
    );
    setChemists((prev) => prev.filter((c) => c.ChemistId !== chemistId));
  };

  const toggleDeliveryManager = async (chemistId, currentValue) => {
    const next = !currentValue;
    setChemists((prev) =>
      prev.map((c) => c.ChemistId === chemistId ? { ...c, IsDeliveryManager: next ? 1 : 0 } : c),
    );
    try {
      await phappsRequest(
        buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.GROUP_CHEMIST_DELIVERY_MANAGER(gid, chemistId)),
        'PATCH',
        { isDeliveryManager: next },
      );
    } catch {
      setChemists((prev) =>
        prev.map((c) => c.ChemistId === chemistId ? { ...c, IsDeliveryManager: currentValue ? 1 : 0 } : c),
      );
      setError('Failed to update delivery manager status');
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: ORANGE, borderRadius: 2 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography fontWeight={700} sx={{ color: ORANGE }}>{group.GroupName}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              label={group.IsActive ? 'Active' : 'Inactive'}
              size="small"
              color={group.IsActive ? 'success' : 'default'}
            />
            <Tooltip title="Rename group">
              <IconButton size="small" onClick={() => onGroupChange(group)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Chemists ({chemists.length})
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{ textTransform: 'none', color: ORANGE, borderColor: ORANGE }}
            variant="outlined"
          >
            Add chemist
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ color: ORANGE }} />
          </Box>
        ) : chemists.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No chemists in this group yet.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', px: 1, pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Pharmacy</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 120, textAlign: 'center' }}>Delivery Manager</Typography>
              <Box sx={{ width: 36 }} />
            </Box>
            {chemists.map((c) => (
              <Box key={c.ChemistId} sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1, borderRadius: 1, '&:hover': { backgroundColor: '#fafafa' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{c.ChemistName}</Typography>
                  {c.ChemistAddress && (
                    <Typography variant="caption" color="text.secondary">{c.ChemistAddress}</Typography>
                  )}
                </Box>
                <Box sx={{ width: 120, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={c.IsDeliveryManager ? 'Remove delivery manager role' : 'Grant delivery manager role'}>
                    <Switch
                      size="small"
                      checked={!!c.IsDeliveryManager}
                      onChange={() => toggleDeliveryManager(c.ChemistId, !!c.IsDeliveryManager)}
                    />
                  </Tooltip>
                </Box>
                <Tooltip title="Remove from group">
                  <IconButton size="small" onClick={() => removeChemist(c.ChemistId)}>
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </>
        )}
      </CardContent>

      <AddChemistDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSearch={searchChemists}
        onAdd={addChemist}
      />
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminChemLinkPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.CHEM_LINK_ADMIN_GROUPS, 'GET');
      setGroups(res?.Groups ?? []);
    } catch (e) {
      setError(e?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const openCreate = () => { setEditingGroup(null); setGroupFormOpen(true); };
  const openEdit = (group) => { setEditingGroup(group); setGroupFormOpen(true); };

  const onGroupSaved = async () => {
    await loadGroups();
    if (editingGroup) {
      const updated = groups.find((g) => g.ChemLinkGroupId === editingGroup.ChemLinkGroupId);
      if (updated) setSelectedGroup(updated);
    }
  };

  const columns = [
    {
      field: 'GroupName',
      headerName: 'Group Name',
      flex: 1,
      minWidth: 140,
      renderCell: (p) => (
        <Typography
          variant="body2"
          fontWeight={selectedGroup?.ChemLinkGroupId === p.row.ChemLinkGroupId ? 700 : 400}
          sx={{ cursor: 'pointer', color: ORANGE }}
          onClick={() => setSelectedGroup(p.row)}
        >
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'IsActive',
      headerName: 'Status',
      width: 90,
      renderCell: (p) => (
        <Chip label={p.value ? 'Active' : 'Inactive'} size="small" color={p.value ? 'success' : 'default'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Edit group">
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditOutlinedIcon fontSize="small" sx={{ color: ORANGE }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const gridRows = groups.map((g) => ({ ...g, id: g.ChemLinkGroupId }));

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingOutlinedIcon sx={{ color: ORANGE, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: ORANGE }}>
              ChemLink Groups
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ backgroundColor: ORANGE, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#d4613c' } }}
          >
            New group
          </Button>
        </Box>

        {error && <Alert severity="warning">{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Card variant="outlined" sx={{ borderColor: ORANGE, borderRadius: 2, flex: '0 0 380px' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight={700} sx={{ color: ORANGE }}>
                  Groups ({groups.length})
                </Typography>
              </Box>
              <Divider />
              <div style={{ height: 400, width: '100%', padding: '0 8px 16px' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress sx={{ color: ORANGE }} />
                  </Box>
                ) : (
                  <DataGrid
                    rows={gridRows}
                    columns={columns}
                    pageSizeOptions={[10, 25]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    onRowClick={(params) => setSelectedGroup(params.row)}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-row': { cursor: 'pointer' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${ORANGE}18` },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fafafa' },
                    }}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {selectedGroup && (
            <Box sx={{ flex: 1 }}>
              <GroupDetail
                key={selectedGroup.ChemLinkGroupId}
                group={selectedGroup}
                onGroupChange={openEdit}
              />
            </Box>
          )}
        </Box>

        <GroupFormDialog
          open={groupFormOpen}
          existing={editingGroup}
          onClose={() => setGroupFormOpen(false)}
          onSaved={onGroupSaved}
        />
      </div>
    </div>
  );
};

export default AdminChemLinkPage;
